import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { useExamNavLock } from '../contexts/ExamContext';
import { useTranslation } from 'react-i18next';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/dropdown-menu';
import { UserCircle, Settings, LogOut, LogIn } from 'lucide-react';
import { OPCION_DEL_MENU, OPACIDAD_SIN_RED } from './panel/MenuDeTresPuntos';

/* pantallas-alumno (B): EL MENU DEL AVATAR CON EL ESTILO DEL MENU DEL PANEL, Y P14 DE LA PARTE 1.
   La caja es la del menu de tres puntos del Panel (fondo #1B1B1B, borde #2C2C2C, radio 12) a 232 px,
   con renglones de 44 (`OPCION_DEL_MENU`): "Settings" con engrane y, tras una linea, "Log out" en rojo.
   P14: durante una sesion de Adaptive, Exam o Flashcards, "Settings" y "Log out" van al 38 % (la misma
   opacidad del menu del Panel apagado), no navegan ni cierran sesion, y al tocarlos sale el MISMO aviso
   que da el menu lateral, porque leen la MISMA senal (`useExamNavLock`, la de Sidebar y la barra de
   abajo). Antes este menu solo se apagaba con el examen (`useSettingsExamLock`); el guardia de la ruta
   /settings sigue siendo solo del examen. Con el invitado se apagan igual "Log in" y "Create Account",
   como en el menu lateral. */
const CAJA_DEL_MENU_DEL_AVATAR = 'w-[232px] rounded-xl border border-[#2C2C2C] bg-[#1B1B1B] p-0 py-1 shadow-xl';
const RENGLON = `${OPCION_DEL_MENU} rounded-none cursor-pointer focus:bg-[#262626]`;
const APAGADO = { opacity: OPACIDAD_SIN_RED };

/**
 * Header account menu (guest: Log in / Create Account · logged-in: Settings / Logout).
 *
 * Built on Radix DropdownMenu instead of the old hand-rolled open/close state: the
 * invisible-overlay dismiss never fired on iOS (WebKit only synthesizes click on
 * "clickable" elements), so the menu could only be closed by re-tapping the icon.
 * Radix listens for pointerdown outside + Escape, and closes on item select —
 * including navigation. Content and testids are unchanged from the old menu; item
 * classes are preserved verbatim via asChild.
 *
 * Items navigate via react-router Link, NEVER raw <a href> (F4.3a-fix, bug
 * "Settings aislada"): a raw anchor triggers a full document reload, which in the
 * Capacitor WebView reboots the whole SPA — the new document re-runs the language
 * boot rule (server profile) and going back restores the OLD document from bfcache
 * with its stale in-memory language. That boundary is what made Settings look
 * language-isolated in both directions on device. Same disease and same fix as the
 * F4.2c bottom-nav Link conversion.
 */
export default function UserMenu({ conNombre = true }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation('common');
  // T1-C3 — candado de Ajustes: SÓLO examen propio en vuelo (ver ExamContext).
  // pantallas-alumno (B), P14: el candado es el de la sesion entera (las tres), el del menu lateral.
  const { isExamActive: enSesion, blockNavigation: avisarSesion } = useExamNavLock();
  // #477 (t7-1b): el hook va ANTES del return temprano de abajo (regla de hooks).
  const navigate = useNavigate();
  const isGuest = user?.is_guest;

  if (!user) return null;

  // #477 (t7-1b): EN LA WEB, CERRAR SESIÓN VA DIRECTO A LA PORTADA. Antes este
  // botón era `onClick={logout}` pelado: nadie navegaba, el guard pintaba /login
  // y ahí se quedaba. Es el mismo arreglo que `Sidebar.js::handleLogout`, y por
  // la misma razón va en el MISMO tick que `logout()`: en web `logout` llega a
  // `setUser(null)` sin un solo `await`, así que usuario y ruta cambian en un
  // solo render y /login nunca llega a montarse. En la app no se navega: la
  // pantalla la sigue poniendo el guard (COS-22-R). Y `logout` ya no recibe el
  // evento del clic como si fueran sus opciones; sólo lee `opts.force`, que un
  // evento no trae, así que la conducta es la misma. Lo fija
  // `logout.sinPestaneo.test.js`.
  const cerrarSesion = () => {
    const cierre = logout();
    if (!IS_APP_BUILD) navigate('/', { replace: true });
    return cierre;
  };

  // Un renglon que navega: con sesion en curso (P14) es un boton inerte al 38 % que avisa; sin
  // sesion, el Link del router de siempre (nunca un <a href> crudo: ver la cabecera).
  // Las clases van en el renglon de Radix (no en el hijo) para que se fundan con las suyas sin chocar.
  const renglonQueNavega = ({ to, testid, Icono, texto, color }) => (
    <DropdownMenuItem asChild className={`${RENGLON} ${color}${enSesion ? ' cursor-not-allowed' : ''}`}>
      {enSesion ? (
        <button type="button" onClick={avisarSesion} aria-disabled="true" style={APAGADO} data-testid={testid}>
          <Icono className="w-4 h-4 shrink-0" aria-hidden="true" /> <span className="truncate">{texto}</span>
        </button>
      ) : (
        <Link to={to} data-testid={testid}>
          <Icono className="w-4 h-4 shrink-0" aria-hidden="true" /> <span className="truncate">{texto}</span>
        </Link>
      )}
    </DropdownMenuItem>
  );

  return (
    <div className="relative" data-testid="user-menu-container">
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center justify-center gap-2.5 min-h-[44px] min-w-[44px] hover:opacity-80 transition-opacity"
            data-testid="user-menu-btn"
          >
            {/* dashboard-rediseno (barra superior): el nombre solo con cuenta y desde 768; el invitado nunca lo ve
                (arriba ya dice "Guest" su chip del plan). El avatar mide 44 en el telefono y 40 desde 640. */}
            {conNombre && !isGuest && (
              <span className="text-[13px] font-medium text-[#C8C8C8] hidden md:block max-w-[180px] truncate" data-testid="user-menu-nombre">
                {user.name || user.email}
              </span>
            )}
            {user.picture ? (
              <img src={user.picture} alt="" className="w-11 h-11 sm:w-10 sm:h-10 rounded-full object-cover" />
            ) : (
              <div className="w-11 h-11 sm:w-10 sm:h-10 rounded-full bg-[#241710] border border-[#3A2414] flex items-center justify-center text-[#F7A06A]">
                <UserCircle className="w-5 h-5" />
              </div>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8} className={CAJA_DEL_MENU_DEL_AVATAR} data-testid="user-dropdown">
          {isGuest ? (
            <>
              <p className="px-3 pt-2 pb-1 text-[12px] font-semibold text-[#9A9A9A]">{t('user_menu.guest_session')}</p>
              {renglonQueNavega({ to: '/login', testid: 'dropdown-login', Icono: LogIn, texto: t('nav.log_in'), color: 'text-[#F4F4F3] focus:text-[#F4F4F3]' })}
              {renglonQueNavega({ to: '/signup', testid: 'dropdown-signup', Icono: UserCircle, texto: t('nav.create_account'), color: 'text-[#F7A06A] focus:text-[#F7A06A]' })}
            </>
          ) : (
            <>
              {/* T1-C3 y P14: durante la sesion el renglon conserva su lugar y su etiqueta pero queda
                  inerte, y el toque muestra el mismo aviso que el menu lateral. */}
              {renglonQueNavega({ to: '/settings', testid: 'dropdown-settings', Icono: Settings, texto: t('user_menu.settings'), color: 'text-[#F4F4F3] focus:text-[#F4F4F3]' })}
              <div className="my-1 h-px bg-[#2C2C2C]" aria-hidden="true" data-parte="linea" />
              <DropdownMenuItem asChild className={`${RENGLON} text-[#F87171] focus:text-[#F87171]${enSesion ? ' cursor-not-allowed' : ''}`}>
                <button type="button" onClick={enSesion ? avisarSesion : cerrarSesion} aria-disabled={enSesion ? 'true' : undefined}
                  style={enSesion ? APAGADO : undefined} data-testid="dropdown-logout">
                  <LogOut className="w-4 h-4 shrink-0" aria-hidden="true" /> <span className="truncate">{t('nav.logout')}</span>
                </button>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
